// =====================================================
// 🔧 TECHNICIAN DASHBOARD - GLOBAL + PERSONAL
// =====================================================

import { useState, useEffect } from 'react'
import { useAuthStore } from '../../stores/authStore'
import { useTheme } from '../../contexts/ThemeContext'
import { useNavigate } from 'react-router-dom'
import {
  Ticket, AlertTriangle, Clock, CheckCircle, 
  TrendingUp, Search, MessageSquare, Zap,
  AlertCircle, Activity, BarChart3, User,
  Award, Target, TrendingDown
} from 'lucide-react'
import axios from 'axios'

// 🌐 DETECCIÓN AUTOMÁTICA DE ENTORNO
const getApiUrl = (): string => {
  if (import.meta.env.VITE_API_URL) {
    return import.meta.env.VITE_API_URL
  }
  
  const isProduction = window.location.hostname !== 'localhost' && window.location.hostname !== '127.0.0.1'
  
  if (isProduction) {
    const protocol = window.location.protocol
    const host = window.location.hostname
    const port = window.location.port || '4343'
    return `${protocol}//${host}:${port}/api`
  }
  
  return 'http://localhost:3001/api'
}

const API_URL = getApiUrl()

interface TicketStats {
  by_status: { status: string; count: number }[]
  by_priority: { priority: string; count: number }[]
  averages: { avg_rating: number; avg_resolution_hours: number }
  recent_tickets: any[]
}

interface MyActivity {
  tickets_worked: number
  tickets_closed: number
  tickets_in_progress: number
  avg_resolution_hours: number
  avg_rating: number
  recent_tickets: any[]
  total_quotations: number
  total_responses: number
}

export default function TechnicianDashboard() {
  const { user } = useAuthStore()
  const { isDark } = useTheme()
  const navigate = useNavigate()
  
  const [globalStats, setGlobalStats] = useState<TicketStats | null>(null)
  const [myActivity, setMyActivity] = useState<MyActivity | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadDashboardData()
    const interval = setInterval(loadDashboardData, 60000)
    return () => clearInterval(interval)
  }, [])

  const loadDashboardData = async () => {
    try {
      const token = localStorage.getItem('token')
      const config = { headers: { Authorization: `Bearer ${token}` } }

      // Cargar estadísticas globales
      const globalRes = await axios.get(`${API_URL}/tickets/stats/dashboard`, config)
      setGlobalStats(globalRes.data.stats || globalRes.data)

      // 🔍 INTENTAR CARGAR MÉTRICAS PERSONALES
      // Si el endpoint no existe, usar datos mock temporales
      try {
        const myActivityRes = await axios.get(`${API_URL}/tickets/stats/my-activity`, config)
        
        // ✅ NULL-SAFETY: Normalizar respuesta
        const activityData = myActivityRes.data || {}
        setMyActivity({
          tickets_worked: activityData.tickets_worked || 0,
          tickets_closed: activityData.tickets_closed || 0,
          tickets_in_progress: activityData.tickets_in_progress || 0,
          avg_resolution_hours: activityData.avg_resolution_hours || 0,
          avg_rating: activityData.avg_rating || 0,
          recent_tickets: activityData.recent_tickets || [],
          total_quotations: activityData.total_quotations || 0,
          total_responses: activityData.total_responses || 0
        })
        
        console.log('✅ [TECHNICIAN] Métricas personales cargadas:', myActivityRes.data)
        
      } catch (err) {
        console.warn('⚠️ [TECHNICIAN] Endpoint /tickets/stats/my-activity no disponible. Usando fallback.')
        console.warn('📋 Error:', err.response?.status, err.response?.data || err.message)
        
        // FALLBACK: Calcular métricas personales del lado cliente
        try {
          const allTicketsRes = await axios.get(`${API_URL}/tickets`, config)
          const allTickets = allTicketsRes.data.tickets || allTicketsRes.data || []
          
          const myTickets = allTickets.filter((t: any) => 
            t.created_by === user?.id || 
            t.assigned_to === user?.id ||
            (t.updates && Array.isArray(t.updates) && t.updates.some((u: any) => u.user_id === user?.id))
          )
          
          const closedByMe = myTickets.filter((t: any) => 
            t.status === 'closed' || t.status === 'resolved'
          )
          
          const inProgressByMe = myTickets.filter((t: any) => 
            t.status === 'in_progress'
          )
          
          // Calcular tiempo promedio
          const ticketsWithTime = closedByMe.filter((t: any) => t.closed_at && t.created_at)
          const avgHours = ticketsWithTime.length > 0
            ? ticketsWithTime.reduce((sum: number, t: any) => {
                const hours = (new Date(t.closed_at).getTime() - new Date(t.created_at).getTime()) / (1000 * 60 * 60)
                return sum + hours
              }, 0) / ticketsWithTime.length
            : 0
          
          // Calcular rating promedio
          const ratedTickets = myTickets.filter((t: any) => t.rating && t.rating > 0)
          const avgRating = ratedTickets.length > 0
            ? ratedTickets.reduce((sum: number, t: any) => sum + (t.rating || 0), 0) / ratedTickets.length
            : 0
          
          setMyActivity({
            tickets_worked: myTickets.length,
            tickets_closed: closedByMe.length,
            tickets_in_progress: inProgressByMe.length,
            avg_resolution_hours: avgHours,
            avg_rating: avgRating,
            recent_tickets: myTickets.slice(0, 5),
            total_quotations: 0,
            total_responses: 0
          })
          
          console.log('✅ [TECHNICIAN] Fallback: Métricas calculadas del lado cliente:', {
            worked: myTickets.length,
            closed: closedByMe.length
          })
          
        } catch (fallbackErr) {
          console.error('❌ [TECHNICIAN] Error en fallback:', fallbackErr)
          // Si falla el fallback, usar valores por defecto
          setMyActivity({
            tickets_worked: 0,
            tickets_closed: 0,
            tickets_in_progress: 0,
            avg_resolution_hours: 0,
            avg_rating: 0,
            recent_tickets: [],
            total_quotations: 0,
            total_responses: 0
          })
        }
      }
    } catch (error) {
      console.error('❌ [TECHNICIAN] Error cargando datos dashboard:', error)
      console.error('📋 Detalles del error:', error.response?.data || error.message)
    } finally {
      setLoading(false)
    }
  }

  const getStatusCount = (status: string) => 
    globalStats?.by_status.find(s => s.status === status)?.count || 0

  const totalTickets = globalStats?.by_status.reduce((sum, s) => sum + s.count, 0) || 0
  const openTickets = getStatusCount('open') + getStatusCount('in_progress')
  const closedTickets = getStatusCount('closed') + getStatusCount('resolved')

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Dashboard Técnico
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Vista global del sistema + Tus métricas personales
          </p>
        </div>
        <button 
          onClick={loadDashboardData}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
        >
          <Activity className="w-4 h-4" />
          Actualizar
        </button>
      </div>

      {/* SECCIÓN 1: ESTADÍSTICAS GLOBALES */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <BarChart3 className="w-5 h-5 text-blue-600 dark:text-blue-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Estadísticas Globales del Sistema
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Total Tickets Global */}
          <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/tickets')}>
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <Ticket className="w-4 h-4 text-blue-600 dark:text-blue-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">{totalTickets}</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Tickets Totales</p>
          </div>

          {/* Abiertos Global */}
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 p-5 rounded-xl border border-orange-200 dark:border-orange-800 hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate('/dashboard/tickets?status=open')}>
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                <AlertCircle className="w-4 h-4 text-orange-600 dark:text-orange-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-orange-900 dark:text-orange-100 mb-1">{openTickets}</p>
            <p className="text-xs text-orange-700 dark:text-orange-300">Abiertos/En Progreso</p>
          </div>

          {/* Cerrados Global */}
          <div className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 p-5 rounded-xl border border-green-200 dark:border-green-800 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600 dark:text-green-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-green-900 dark:text-green-100 mb-1">{closedTickets}</p>
            <p className="text-xs text-green-700 dark:text-green-300">Resueltos/Cerrados</p>
          </div>

          {/* Tiempo Promedio Global */}
          <div className="bg-white dark:bg-gray-900 p-5 rounded-xl border border-gray-200 dark:border-gray-800 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg">
                <Clock className="w-4 h-4 text-purple-600 dark:text-purple-400" />
              </div>
            </div>
            <p className="text-2xl font-bold text-gray-900 dark:text-white mb-1">
              {Math.round(globalStats?.averages.avg_resolution_hours || 0)}h
            </p>
            <p className="text-xs text-gray-600 dark:text-gray-400">Tiempo Promedio</p>
          </div>
        </div>
      </div>

      {/* SECCIÓN 2: MIS MÉTRICAS PERSONALES */}
      <div>
        <div className="flex items-center gap-2 mb-4">
          <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
            Mis Métricas Personales
          </h2>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Mis Tickets Trabajados */}
          <div className="bg-gradient-to-br from-indigo-50 to-indigo-100 dark:from-indigo-900/20 dark:to-indigo-800/20 p-5 rounded-xl border border-indigo-200 dark:border-indigo-800">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                <Target className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
              </div>
              <span className="text-xs font-medium text-indigo-700 dark:text-indigo-400">Total</span>
            </div>
            <p className="text-3xl font-bold text-indigo-900 dark:text-indigo-100 mb-1">
              {myActivity?.tickets_worked || 0}
            </p>
            <p className="text-sm text-indigo-700 dark:text-indigo-300">Tickets Trabajados</p>
          </div>

          {/* Mis Tickets Cerrados */}
          <div className="bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-900/20 dark:to-emerald-800/20 p-5 rounded-xl border border-green-200 dark:border-green-800">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
              </div>
              <span className="text-xs font-medium text-green-700 dark:text-green-400">Resueltos</span>
            </div>
            <p className="text-3xl font-bold text-green-900 dark:text-green-100 mb-1">
              {myActivity?.tickets_closed || 0}
            </p>
            <p className="text-sm text-green-700 dark:text-green-300">Por Mí</p>
            {myActivity && myActivity.tickets_worked > 0 && (
              <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                {((myActivity.tickets_closed / myActivity.tickets_worked) * 100).toFixed(0)}% efectividad
              </p>
            )}
          </div>

          {/* Mi Tiempo Promedio */}
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 p-5 rounded-xl border border-purple-200 dark:border-purple-800">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                <Clock className="w-5 h-5 text-purple-600 dark:text-purple-400" />
              </div>
              <span className="text-xs font-medium text-purple-700 dark:text-purple-400">Personal</span>
            </div>
            <p className="text-3xl font-bold text-purple-900 dark:text-purple-100 mb-1">
              {Math.round(myActivity?.avg_resolution_hours || 0)}h
            </p>
            <p className="text-sm text-purple-700 dark:text-purple-300">Mi Tiempo Promedio</p>
            {globalStats && myActivity && (
              <p className={`text-xs mt-2 flex items-center gap-1 ${
                myActivity.avg_resolution_hours < globalStats.averages.avg_resolution_hours 
                  ? 'text-green-600 dark:text-green-400' 
                  : 'text-orange-600 dark:text-orange-400'
              }`}>
                {myActivity.avg_resolution_hours < globalStats.averages.avg_resolution_hours 
                  ? <TrendingDown className="w-3 h-3" />
                  : <TrendingUp className="w-3 h-3" />
                }
                {Math.abs(((myActivity.avg_resolution_hours - globalStats.averages.avg_resolution_hours) / globalStats.averages.avg_resolution_hours) * 100).toFixed(0)}% vs promedio
              </p>
            )}
          </div>

          {/* Mi Rating Promedio */}
          <div className="bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-yellow-900/20 dark:to-amber-800/20 p-5 rounded-xl border border-yellow-200 dark:border-yellow-800">
            <div className="flex items-center justify-between mb-3">
              <div className="p-2 bg-white dark:bg-gray-800 rounded-lg">
                <Award className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
              </div>
              <span className="text-xs font-medium text-yellow-700 dark:text-yellow-400">Calidad</span>
            </div>
            <p className="text-3xl font-bold text-yellow-900 dark:text-yellow-100 mb-1">
              {myActivity?.avg_rating ? myActivity.avg_rating.toFixed(1) : '0.0'}
            </p>
            <p className="text-sm text-yellow-700 dark:text-yellow-300">Mi Rating Promedio</p>
            <div className="flex items-center gap-1 mt-2">
              {[1, 2, 3, 4, 5].map((star) => (
                <div
                  key={star}
                  className={`w-3 h-3 rounded-full ${
                    star <= (myActivity?.avg_rating || 0)
                      ? 'bg-yellow-500'
                      : 'bg-gray-300 dark:bg-gray-600'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* SECCIÓN 3: MIS ÚLTIMOS TICKETS */}
      {myActivity && myActivity.recent_tickets && myActivity.recent_tickets.length > 0 && (
        <div className="bg-white dark:bg-gray-900 p-6 rounded-xl border border-gray-200 dark:border-gray-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
              Mis Últimos Tickets Trabajados
            </h2>
            <button 
              onClick={() => navigate('/dashboard/tickets')}
              className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
            >
              Ver todos
            </button>
          </div>
          
          <div className="space-y-3">
            {myActivity.recent_tickets.slice(0, 5).map((ticket) => (
              <div 
                key={ticket.id}
                onClick={() => navigate(`/dashboard/tickets/${ticket.id}`)}
                className="flex items-center justify-between p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                    ticket.priority === 'high' ? 'bg-red-100 dark:bg-red-900/30' :
                    ticket.priority === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30' :
                    'bg-green-100 dark:bg-green-900/30'
                  }`}>
                    <Ticket className={`w-5 h-5 ${
                      ticket.priority === 'high' ? 'text-red-600 dark:text-red-400' :
                      ticket.priority === 'medium' ? 'text-yellow-600 dark:text-yellow-400' :
                      'text-green-600 dark:text-green-400'
                    }`} />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">
                      {ticket.ticket_number || `#${ticket.id}`}
                    </p>
                    <p className="text-sm text-gray-500 dark:text-gray-400 truncate max-w-md">
                      {ticket.subject || ticket.description?.substring(0, 50)}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    ticket.status === 'closed' || ticket.status === 'resolved'
                      ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                      : ticket.status === 'in_progress'
                      ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400'
                      : 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-400'
                  }`}>
                    {ticket.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Acceso Rápido */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <button
          onClick={() => navigate('/dashboard/tickets')}
          className="p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-xl hover:bg-blue-100 dark:hover:bg-blue-900/30 transition-colors"
        >
          <Ticket className="w-6 h-6 text-blue-600 dark:text-blue-400 mb-2" />
          <p className="font-medium text-gray-900 dark:text-white">Ver Todos los Tickets</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Gestionar casos del sistema</p>
        </button>

        <button
          onClick={() => navigate('/dashboard/chat')}
          className="p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl hover:bg-green-100 dark:hover:bg-green-900/30 transition-colors"
        >
          <MessageSquare className="w-6 h-6 text-green-600 dark:text-green-400 mb-2" />
          <p className="font-medium text-gray-900 dark:text-white">Chat de Soporte</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Asistencia técnica IA</p>
        </button>

        <button
          onClick={() => navigate('/dashboard/docs')}
          className="p-4 bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 rounded-xl hover:bg-purple-100 dark:hover:bg-purple-900/30 transition-colors"
        >
          <Search className="w-6 h-6 text-purple-600 dark:text-purple-400 mb-2" />
          <p className="font-medium text-gray-900 dark:text-white">Documentación</p>
          <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">Manuales Caterpillar</p>
        </button>
      </div>
    </div>
  )
}